<template>
  <HeaderBar
    :modifiers="['narrow', 'darkest']"
    class="hide-for-medium"
  >
    <p v-if="users">
      {{ userCountMobile }}
    </p>
    <template #right>
      <Select
        :placeholder="$t('general.sort_by')"
        :items="sortByOptions"
        v-model="sortBy"
      />
    </template>
  </HeaderBar>

  <div class="table-container">
    <table class="table">
      <!-- table header -->
      <UserTableHeader>
        <UserTableHeaderCell
          :content="$t('user_operations.name')"
          :aria-label="$t('user_operations.name')"
          @click="sortUsers('name')"
        />
        <UserTableHeaderCell
          :content="$t('user_operations.email')"
          :aria-label="$t('user_operations.email')"
          @click="sortUsers('email')"
        />
        <UserTableHeaderCell :content="$t('user_operations.last_seen')" />
        <UserTableHeaderCell :content="$t('user_operations.roles')" />
      </UserTableHeader>
      <!-- table body -->
      <tbody>
        <tr v-for="user in users" :key="user.name" class="table__row">
          <UserTableBodyCell
            :content="fullName(user)"
            :routePath="userRoute(user, $route)"
            :mobileStack="[user.email, `${$t('user_operations.last_seen')}: ${lastSeen(user)}`]"
            :aria-label="$t('user_operations.name')"
          />
          <UserTableBodyCell
            :content="user.email"
            :routePath="userRoute(user, $route)"
            :aria-label="$t('user_operations.email')"
            :modifiers="['dark', 'font-normal']"
            class="show-for-large"
          />
          <UserTableBodyCell
            :content="lastSeen(user)"
            :routePath="userRoute(user, $route)"
            :aria-label="$t('user_operations.last_seen')"
            :modifiers="['dark', 'font-normal']"
            class="show-for-large"
          />
          <UserTableBodyCell>
            <ul class="role-icons">
              <template v-for="role in roles" :key="role">
                <li
                  v-if="role !== 'no_access'"
                  :class="{
                    'role-icons__icon': true,
                    'role-icons__icon--active': userPermissions(user.roles, role)
                  }"
                >
                  <InfoPopup>
                    <template #trigger>
                      <UseSvg :name="roleIcons[role]" class="icon" />
                    </template>
                    <div class="tooltip">
                      <div class="tooltip__title">
                        <div class="typo-tiny">
                          {{ $t(`roles.name.${role}`) }}
                        </div>
                      </div>
                      <div class="tooltip__content">
                        <div class="typo-tiny">
                          {{ userPermissions(user.roles, role) }}
                        </div>
                      </div>
                    </div>
                  </InfoPopup>
                </li>
              </template>
            </ul>
          </UserTableBodyCell>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup>
import { ref, watch, computed } from 'vue';

// Utils
import I18n from 'i18n-js';
import pluralize from '@/functions/pluralize';
import { formatDateLongMonth } from '@/functions/date_format_helpers';

// Components
import HeaderBar from '@/components/generic/header_bar.vue';
import InfoPopup from '@/components/generic/info_popup.vue';
import Select from '@/components/form/select.vue';
import UseSvg from '@/components/generic/use_svg.vue';
import UserTableHeader from '@/components/users/user_table/user_table_header.vue';
import UserTableHeaderCell from '@/components/users/user_table/user_table_header_cell.vue';
import UserTableBodyCell from '@/components/users/user_table/user_table_body_cell.vue';

const props = defineProps({
  users: { type: Array, required: true },
  roles: { type: Array, required: true },
});

/**
 * Sort select options
 */
const sortByOptions = [
  { value: 'name', label: I18n.t('user_operations.name') },
  { value: 'email', label: I18n.t('user_operations.email') },
];

/**
 * Role icons
 */
const roleIcons = {
  operation_administrator: 'wrench',
  trusted_inspector: 'verified_bubble',
  inspector: 'hard_hat',
  observer: 'eye',
  administrator: 'wrench',
  planner: 'checkboard',
};

const sortBy = ref(null);

const userCountMobile = computed(() => {
  return pluralize(
    props.users.length,
    I18n.t('user_operations.user').toLowerCase(),
    I18n.t('user_operations.users').toLowerCase()
  );
});

/**
 * Watches for changes on the sort by dropdown
 */
watch(sortBy, () => {
  sortUsers(sortBy.value);
});

function sortUsers(value) {
  if (value === 'name') {
    props.users.sort((a, b) => a.first_name.localeCompare(b.first_name));
  } else {
    props.users.sort((a, b) => a.email.localeCompare(b.email));
  }
}

/**
 * Display active state and permissions in role tooltip
 */
function userPermissions(userRoles, role) {
  if (userRoles[role] !== undefined) {
    return userRoles[role].join(', ');
  }
  return false;
}

/**
 * Full name or unverified ('- email')
 */
function fullName(user) {
  if (user.first_name) {
    return `${user.first_name} ${user.last_name}`;
  } else {
    return `- ${user.email}`;
  }
}

/**
 * Route path to edit user permissions
 */
function userRoute(user, route) {
  return {
    name: 'editUser',
    params: {
      organisationId: route.params.organisationId,
      userId: user.id,
    },
  };
}

/**
 * When the user last signed in
 */
function lastSeen(user) {
  return user.last_sign_in_at ? formatDateLongMonth(user.last_sign_in_at) : '-';
}
</script>
